'use client'

import { useState, useEffect } from 'react'
import { Card } from '@/components/ui/card'
import { Loader2, Cpu, RefreshCw } from 'lucide-react'
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, Cell,
} from 'recharts'
import apiClient from '@/lib/noc-api-client'
import { useLanguage } from '@/contexts/LanguageContext'

interface FamilyEntry {
  family: string
  online: number
  dormant: number
  alerting: number
  offline: number
  total: number
  models: string[]
  availability_pct: number
}

interface Summary {
  online: number
  dormant: number
  alerting: number
  offline: number
}

interface ApiResponse {
  families: FamilyEntry[]
  total_devices: number
  summary: Summary
}

interface TooltipProps {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}

function CustomTooltip({ active, payload, label }: TooltipProps) {
  const { t } = useLanguage()
  const df = t.deviceFleet
  if (!active || !payload?.length) return null
  const total = payload.reduce((s, p) => s + (p.value || 0), 0)
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[8px] shadow-md px-3 py-2 text-[12px] min-w-[160px]">
      <p className="font-medium text-[#101828] mb-1.5">{label}</p>
      {payload.map(p => (
        <div key={p.name} className="flex justify-between gap-4 text-[#4A5565]">
          <span className="flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm" style={{ background: p.color }} />
            {p.name}
          </span>
          <span className="font-medium text-[#101828]">{p.value}</span>
        </div>
      ))}
      <div className="border-t border-[#F0F0F0] mt-1 pt-1 flex justify-between text-[#4A5565]">
        <span>{df.total}</span>
        <span className="font-semibold text-[#101828]">{total}</span>
      </div>
    </div>
  )
}

const STATUS_COLORS = {
  online:   '#0BBE14',
  dormant:  '#2B7FFF',
  alerting: '#F59E0B',
  offline:  '#E5E7EB',
}

function shortFamily(name: string, max = 14): string {
  return name.length > max ? name.slice(0, max - 1) + '…' : name
}

export function DeviceFleetWidget() {
  const { t } = useLanguage()
  const df = t.deviceFleet
  const [data, setData]     = useState<ApiResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError]   = useState<string | null>(null)

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)
      const res: ApiResponse = await apiClient('/api/dashboard/device-fleet')
      setData(res)
    } catch (err) {
      console.error('DeviceFleet fetch error:', err)
      setError('Failed to load device fleet data')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchData()
    // Auto-refresh every 60 seconds (live data)
    const interval = setInterval(() => { fetchData() }, 60000)
    return () => clearInterval(interval)
  }, [])

  const chartData = (data?.families ?? []).map(f => ({
    name: shortFamily(f.family),
    fullName: f.family,
    [df.online]: f.online,
    [df.dormant]: f.dormant,
    [df.alerting]: f.alerting,
    [df.offline]: f.offline,
    total: f.total,
    models: f.models,
    availability_pct: f.availability_pct,
  }))

  const summary = data?.summary
  const totalDevices = data?.total_devices ?? 0

  return (
    <Card className="bg-white border border-[#E5E7EB] shadow-none rounded-[10px] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-[#4A5565]" />
          <span className="text-[15px] font-medium text-[#101828] tracking-[-0.2px]">
            {df.title}
          </span>
          <span className="text-[11px] text-[#939393] font-normal ml-1">
            {df.subtitle}
          </span>
        </div>
        <button onClick={fetchData} className="p-1.5 rounded-[6px] hover:bg-[#F5F5F5] transition-colors" title="Refresh">
          <RefreshCw className="h-3.5 w-3.5 text-[#939393]" />
        </button>
      </div>

      {/* Summary pills */}
      {!loading && !error && summary && (
        <div className="flex items-center gap-3 mb-3 text-[10px]">
          {[
            { label: df.online,   value: summary.online,   color: '#0BBE14', bg: '#DCFCE7' },
            { label: df.dormant,  value: summary.dormant,  color: '#2B7FFF', bg: '#DBEAFE' },
            { label: df.alerting, value: summary.alerting, color: '#F59E0B', bg: '#FEF3C7' },
            { label: df.offline,  value: summary.offline,  color: '#9CA3AF', bg: '#F3F4F6' },
          ].map(s => (
            <span key={s.label} className="flex items-center gap-1 rounded-full px-2 py-0.5 font-medium" style={{ background: s.bg, color: s.color }}>
              <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
              {s.value} {s.label}
            </span>
          ))}
          <span className="ml-auto text-[#939393]">{totalDevices} {df.totalDevices}</span>
        </div>
      )}

      {/* Chart */}
      {loading ? (
        <div className="h-[200px] flex items-center justify-center gap-2 text-[13px] text-[#939393]">
          <Loader2 className="h-4 w-4 animate-spin" /> {df.loading}
        </div>
      ) : error ? (
        <div className="h-[200px] flex items-center justify-center text-[13px] text-[#CD585A]">{error}</div>
      ) : chartData.length === 0 ? (
        <div className="h-[200px] flex flex-col items-center justify-center gap-1 text-center">
          <p className="text-[13px] text-[#4A5565]">{df.noData}</p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={chartData} margin={{ top: 2, right: 8, left: 0, bottom: 4 }} barCategoryGap="25%" barGap={1}>
            <CartesianGrid strokeDasharray="3 3" stroke="#F0F0F0" vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 9, fill: '#939393' }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              allowDecimals={false}
              tick={{ fontSize: 10, fill: '#939393' }}
              tickLine={false}
              axisLine={false}
              width={24}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#F9FAFB' }} />
            <Legend wrapperStyle={{ fontSize: 11, color: '#4A5565', paddingTop: 6 }} />
            <Bar dataKey={df.online}   stackId="s" fill={STATUS_COLORS.online}   radius={[0,0,0,0]} />
            <Bar dataKey={df.dormant}  stackId="s" fill={STATUS_COLORS.dormant}   radius={[0,0,0,0]} />
            <Bar dataKey={df.alerting} stackId="s" fill={STATUS_COLORS.alerting}  radius={[0,0,0,0]} />
            <Bar dataKey={df.offline}  stackId="s" fill={STATUS_COLORS.offline}   radius={[3,3,0,0]}>
              {chartData.map((_, i) => (
                <Cell key={`offline-${i}`} fill="#D1D5DB" />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </Card>
  )
}
